import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/App';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({ termsPrivacy: false, transactional: false, marketing: false });
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consents.termsPrivacy) { toast.error('You must agree to the Terms of Use and Privacy Policy'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.phone || null);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="border-b border-ink-200 bg-paper/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="register-logo-link">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <Link to="/" className="text-sm text-ink-600 hover:text-ink flex items-center gap-1.5" data-testid="register-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 grid-bg">
        <div className="w-full max-w-md py-8">
          <div className="card-base p-8">
            <p className="eyebrow mb-3">CREATE ACCOUNT</p>
            <h1 className="h2 mb-1">Take back the calendar.</h1>
            <p className="text-ink-600 text-sm mb-6">Free forever. Up to 3 renewals. No credit card.</p>

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="btn btn-outline w-full mb-4"
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-ink-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 eyebrow">OR EMAIL</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="eyebrow mb-2 block">Full name <span className="text-respondent">*</span></label>
                <input id="name" type="text" placeholder="Jane Doe" className="input-editorial"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required data-testid="register-name-input"/>
              </div>
              <div>
                <label htmlFor="email" className="eyebrow mb-2 block">Email <span className="text-respondent">*</span></label>
                <input id="email" type="email" placeholder="name@example.com" className="input-editorial"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required data-testid="register-email-input"/>
              </div>
              <div>
                <label htmlFor="phone" className="eyebrow mb-2 block">Phone (optional)</label>
                <input id="phone" type="tel" placeholder="+1 (555) 123-4567" className="input-editorial"
                  value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="register-phone-input"/>
              </div>
              <div>
                <label htmlFor="password" className="eyebrow mb-2 block">Password <span className="text-respondent">*</span></label>
                <input id="password" type="password" placeholder="••••••••" className="input-editorial"
                  value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required minLength={6} data-testid="register-password-input"/>
                <p className="text-[11px] text-ink-600 mt-1.5 font-mono">MIN. 6 CHARACTERS</p>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="eyebrow mb-2 block">Confirm password <span className="text-respondent">*</span></label>
                <input
                  id="confirmPassword" type="password" placeholder="••••••••"
                  className={`input-editorial ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-respondent' : ''}`}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required data-testid="register-confirm-password-input"
                />
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-respondent mt-1.5">Passwords do not match</p>
                )}
              </div>

              <div className="space-y-3 pt-3 border-t border-ink-200">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms-privacy-consent"
                    checked={consents.termsPrivacy}
                    onCheckedChange={(checked) => setConsents({ ...consents, termsPrivacy: checked })}
                    className="mt-0.5"
                    data-testid="terms-privacy-checkbox"
                  />
                  <label htmlFor="terms-privacy-consent" className="text-xs text-ink-600 leading-relaxed cursor-pointer">
                    I agree to the{' '}
                    <Link to="/terms" className="text-respondent underline font-medium">Terms of Use</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-respondent underline font-medium">Privacy Policy</Link>
                    {' '}<span className="text-respondent">*</span>
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="transactional-consent"
                    checked={consents.transactional}
                    onCheckedChange={(checked) => setConsents({ ...consents, transactional: checked })}
                    className="mt-0.5"
                    data-testid="transactional-checkbox"
                  />
                  <label htmlFor="transactional-consent" className="text-xs text-ink-600 leading-relaxed cursor-pointer">
                    I consent to receive transactional messages from Enota (account confirmations, renewal reminders, status notifications).
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="marketing-consent"
                    checked={consents.marketing}
                    onCheckedChange={(checked) => setConsents({ ...consents, marketing: checked })}
                    className="mt-0.5"
                    data-testid="marketing-checkbox"
                  />
                  <label htmlFor="marketing-consent" className="text-xs text-ink-600 leading-relaxed cursor-pointer">
                    I agree to receive marketing messages from Enota (promotional offers and updates). <span className="text-ink-600/60">(Optional)</span>
                  </label>
                </div>

                <p className="text-[10px] text-ink-600/80 leading-relaxed pl-3 border-l-2 border-ink-200 font-mono">
                  SMS opt-in is optional. Up to 4 messages per month, msg/data rates apply, text STOP to opt out, HELP for help, contact{' '}
                  <a href="mailto:support@renewalnote.com" className="text-respondent underline">support@renewalnote.com</a>. Carriers not liable. Data not sold or shared.{' '}
                  <Link to="/sms-terms" className="text-respondent underline">SMS Terms</Link>.
                </p>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading || !consents.termsPrivacy}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <p className="text-center text-sm text-ink-600 mt-6">
              Already a member?{' '}
              <Link to="/login" className="text-respondent font-medium hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
          <p className="text-center mt-6 font-mono text-[10px] tracking-widest text-ink-600">
            ENOTA://AUTH · SECURE [TLS 1.3]
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
